import type { WsMessage, WsSubscribeFilter } from '../types/cap'

export type WsHandler = (msg: WsMessage) => void

export class AlertWebSocket {
  private ws: WebSocket | null = null
  private handlers: WsHandler[] = []
  private pingInterval: ReturnType<typeof setInterval> | null = null
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private stopped = false
  private filter: WsSubscribeFilter
  private rapidFailCount = 0
  private connectTime = 0

  onConnected?: () => void
  onDisconnected?: () => void
  onAuthError?: () => void

  constructor(filter: WsSubscribeFilter = {}) {
    this.filter = filter
  }

  connect(token?: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) return
    this.stopped = false

    const rawBase: string = import.meta.env.VITE_WS_URL ?? '/ws'
    const wsBase = rawBase.startsWith('/')
      ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${rawBase}`
      : rawBase
    const url = new URL(`${wsBase}/alerts`)
    if (token) url.searchParams.set('token', token)

    this.connectTime = Date.now()
    this.ws = new WebSocket(url.toString())

    this.ws.addEventListener('open', () => {
      this.rapidFailCount = 0
      this.ws!.send(JSON.stringify({ type: 'subscribe', filter: this.filter }))
      this.onConnected?.()
      this.pingInterval = setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'ping' }))
        }
      }, 30_000)
    })

    this.ws.addEventListener('message', (evt) => {
      try {
        const msg = JSON.parse(evt.data as string) as WsMessage
        this.handlers.forEach((h) => h(msg))
      } catch {
        // ignore malformed frames
      }
    })

    this.ws.addEventListener('close', (evt) => {
      this.cleanup()
      this.onDisconnected?.()
      if (evt.code === 4001) {
        this.stopped = true
        this.onAuthError?.()
        return
      }
      // A close within 2s of connecting (code 1006) is almost certainly a server
      // rejection (e.g. HTTP 403 for an expired token). After 3 such rapid
      // failures, stop retrying and force re-login.
      if (Date.now() - this.connectTime < 2000) {
        this.rapidFailCount++
        if (this.rapidFailCount >= 3) {
          this.rapidFailCount = 0
          this.stopped = true
          this.onAuthError?.()
          return
        }
      } else {
        this.rapidFailCount = 0
      }
      if (!this.stopped) {
        this.reconnectTimeout = setTimeout(() => this.connect(token), 5_000)
      }
    })

    this.ws.addEventListener('error', () => {
      this.ws?.close()
    })
  }

  disconnect(): void {
    this.stopped = true
    this.cleanup()
    this.ws?.close()
    this.ws = null
  }

  updateFilter(filter: WsSubscribeFilter): void {
    this.filter = filter
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'subscribe', filter }))
    }
  }

  addHandler(handler: WsHandler): () => void {
    this.handlers.push(handler)
    return () => {
      this.handlers = this.handlers.filter((h) => h !== handler)
    }
  }

  private cleanup(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
  }
}
